import { wait } from '@change-corgi/core/async';
import type { UtilityContext } from '@change-corgi/core/react/utilityContext';

import type {
	StarterDashboardV2ShareRecommendationsQuery,
	StarterDashboardV2ShareRecommendationsQueryVariables,
} from './shareRecommendations.graphql';
import { StarterDashboardV2ShareRecommendations } from './shareRecommendations.graphql';

export type RootShareRecommendations = StarterDashboardV2ShareRecommendationsQuery['shareRecommendations'];
export type FacebookShareRecommendations =
	StarterDashboardV2ShareRecommendationsQuery['shareRecommendations']['facebook'];
export type RedditShareRecommendations = StarterDashboardV2ShareRecommendationsQuery['shareRecommendations']['reddit'];

const RETRY_DELAYS = [5000, 5000, 10000, 10000]; // in milliseconds

async function retryIfNeeded(
	data: RootShareRecommendations,
	slug: string,
	utilityContext: UtilityContext,
	retryCount: number = 0,
) {
	const hasRecommendations = data.facebook.length > 0 || data.reddit.length > 0;

	if (hasRecommendations) {
		return data;
	}

	if (retryCount > RETRY_DELAYS.length) {
		return { reddit: [], facebook: [] }; // Return empty recommendations if all retries fail
	}

	await wait(RETRY_DELAYS[retryCount]);

	const newData = await fetchShareRecommendations(utilityContext, slug);
	return retryIfNeeded(newData, slug, utilityContext, retryCount + 1);
}

async function fetchShareRecommendations(
	utilityContext: UtilityContext,
	slug: string,
): Promise<RootShareRecommendations> {
	const {
		gql: { fetch },
	} = utilityContext;

	const { data } = await fetch<
		StarterDashboardV2ShareRecommendationsQuery,
		StarterDashboardV2ShareRecommendationsQueryVariables
	>({
		query: StarterDashboardV2ShareRecommendations,
		variables: { slug },
		rejectOnError: true,
	});

	return data.shareRecommendations;
}

export async function getShareRecommendations(
	utilityContext: UtilityContext,
	slug: string,
): Promise<RootShareRecommendations> {
	const firstLoad = await fetchShareRecommendations(utilityContext, slug);

	return retryIfNeeded(firstLoad, slug, utilityContext);
}
